"use client";

import React, { useState } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Calculator, Layers, Sparkles, Scale, CircleCheck as CheckCircle2, ArrowRight, House as Home, Compass, ClipboardCheck, FileText } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
interface PillarItem {
  id: string;
  iconName: string;
  title: string;
  summary: string;
  detail: string;
  tag: string;
}
interface MilestoneItem {
  id: string;
  step: string;
  title: string;
  timing: string;
  description: string;
  checklistNote: string;
}
const planningPillars: PillarItem[] = [{
  id: "budget",
  iconName: "calculator",
  title: "Establishing realistic budgets",
  summary: "Plan for real costs and build in cushion before demolition begins.",
  detail: "We recommend a 15% to 20% contingency budget for older Decatur homes, alongside clear fixture allowances and local permitting allocations.",
  tag: "Financial Prep"
}, {
  id: "priorities",
  iconName: "layers",
  title: "Setting project priorities",
  summary: "Balance structural longevity with day-to-day visual upgrades.",
  detail: "Distinguish structural and mechanical needs (like plumbing, wiring, and insulation) from aesthetic touches to protect your home's foundation and long-term resale value.",
  tag: "Scope Clarity"
}, {
  id: "finishes",
  iconName: "sparkles",
  title: "Selecting finishes early",
  summary: "Lock in lead times on custom cabinetry, tile, and plumbing fixtures.",
  detail: "Securing key materials ahead of framing prevents mid-construction downtime and protects your project timeline from supplier backorders.",
  tag: "Procurement"
}, {
  id: "permits",
  iconName: "scale",
  title: "Navigating Decatur building codes",
  summary: "Ensure full compliance with City of Decatur zoning and historic standards.",
  detail: "From historic district guidelines to setback rules and stormwater management, we prepare and submit all permit packages directly to local city planners.",
  tag: "City Compliance"
}];
const checklistMilestones: MilestoneItem[] = [{
  id: "milestone-1",
  step: "01",
  title: "Initial Space Assessment & Feasibility",
  timing: "Weeks 1–2",
  description: "On-site review of load-bearing walls, existing plumbing lines, electrical capacity, and floor plan flow.",
  checklistNote: "Verify structural boundaries and discuss your primary goals."
}, {
  id: "milestone-2",
  step: "02",
  title: "Architectural Drafting & Layout Optimization",
  timing: "Weeks 3–4",
  description: "Translating your wish list into detailed floor plans, elevation drawings, and cabinet spatial configurations.",
  checklistNote: "Review 2D and 3D schematics to confirm traffic flow."
}, {
  id: "milestone-3",
  step: "03",
  title: "Material Board & Finish Specifications",
  timing: "Weeks 5–6",
  description: "Finalizing countertop slabs, cabinetry door profiles, hardwood stains, tile patterns, and lighting fixtures.",
  checklistNote: "Sign off on itemized finish schedules and lead times."
}, {
  id: "milestone-4",
  step: "04",
  title: "Pre-Construction Walkthrough & Site Protection",
  timing: "1 Week Prior",
  description: "Final pre-demo alignment with your project lead, setting dust barriers, floor protection, and dumpster staging.",
  checklistNote: "Confirm access logistics and direct communication channels."
}];
export default function Planning() {
  const shouldReduceMotion = useReducedMotion();
  const [activeTab, setActiveTab] = useState<string>("all");
  const getPillarIcon = (icon: string) => {
    switch (icon) {
      case "calculator":
        return <Calculator className="size-5 text-primary" />;
      case "layers":
        return <Layers className="size-5 text-primary" />;
      case "sparkles":
        return <Sparkles className="size-5 text-primary" />;
      case "scale":
        return <Scale className="size-5 text-primary" />;
      default:
        return <Compass className="size-5 text-primary" />;
    }
  };
  return <section id="planning-your-remodel" data-nav="light" className="relative bg-background py-16 sm:py-20 lg:py-24 overflow-hidden">
      {/* Decorative architectural background grid accents */}
      <div aria-hidden="true" className="pointer-events-none absolute inset-0 opacity-40 [background-image:linear-gradient(to_right,hsl(38_18%_86%/0.35)_1px,transparent_1px),linear-gradient(to_bottom,hsl(38_18%_86%/0.35)_1px,transparent_1px)] [background-size:4rem_4rem]" />

      <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="mx-auto max-w-3xl text-center">
          <motion.div initial={shouldReduceMotion ? {
          opacity: 1
        } : {
          opacity: 0,
          y: 16
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.4,
          ease: [0.16, 1, 0.3, 1]
        }}>
            <Badge variant="outline" className="border-border bg-secondary/80 text-foreground font-semibold uppercase tracking-wider px-3.5 py-1 text-xs mb-4">
              Pre-Construction Advice
            </Badge>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-serif font-semibold text-foreground tracking-tight leading-tight">
              Planning your remodel with intention
            </h2>
            <p className="mt-4 text-base sm:text-lg text-muted-foreground leading-relaxed">
              Careful preparation before demolition saves time, protects your budget, and prevents mid-project delays. Here is how we help you lay a solid foundation.
            </p>
          </motion.div>
        </div>

        {/* 4 Planning Pillars Grid */}
        <div className="mt-14 sm:mt-16 grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8">
          {planningPillars.map((pillar, index) => <motion.div key={pillar.id} data-index={index} initial={shouldReduceMotion ? {
          opacity: 1
        } : {
          opacity: 0,
          y: 16
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.4,
          delay: shouldReduceMotion ? 0 : index * 0.08,
          ease: [0.16, 1, 0.3, 1]
        }}>
              <Card className="h-full border border-border bg-card/90 backdrop-blur-md transition-[color,background-color,border-color,box-shadow] duration-300 hover:border-primary/40 hover:shadow-md">
                <CardHeader className="gap-3">
                  <div className="flex items-center justify-between">
                    <div className="flex size-10 items-center justify-center rounded-lg bg-secondary">
                      {getPillarIcon(pillar.iconName)}
                    </div>
                    <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                      {pillar.tag}
                    </span>
                  </div>
                  <CardTitle className="text-xl font-serif text-foreground font-semibold mt-2">
                    {pillar.title}
                  </CardTitle>
                  <CardDescription className="text-sm font-medium text-foreground/80">
                    {pillar.summary}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-muted-foreground leading-relaxed border-t border-border pt-4">
                    {pillar.detail}
                  </p>
                </CardContent>
              </Card>
            </motion.div>)}
        </div>

        {/* Two-Column Detail Section: Checklist & Proactive Advice */}
        <div className="mt-16 sm:mt-20 grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-start">
          {/* Interactive Checklist Accordion (7 Columns) */}
          <motion.div className="lg:col-span-7 bg-card border border-border rounded-xl p-6 sm:p-8 shadow-sm" initial={shouldReduceMotion ? {
          opacity: 1
        } : {
          opacity: 0,
          y: 16
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.4,
          ease: [0.16, 1, 0.3, 1]
        }}>
            <div className="flex items-center gap-3 pb-6 border-b border-border">
              <div className="flex size-9 items-center justify-center rounded-md bg-secondary text-primary">
                <ClipboardCheck className="size-5" />
              </div>
              <div>
                <h3 className="text-xl font-serif font-semibold text-foreground">
                  Pre-construction checklist
                </h3>
                <p className="text-xs sm:text-sm text-muted-foreground">
                  Step-by-step milestones before crew arrival on day one
                </p>
              </div>
            </div>

            <Accordion type="single" collapsible defaultValue="milestone-1" className="mt-4">
              {checklistMilestones.map((item, index) => <AccordionItem key={item.id} value={item.id} data-index={index} className="border-border py-1">
                  <AccordionTrigger className="hover:no-underline py-4 text-left group">
                    <div className="flex items-center gap-3 pr-2">
                      <span className="flex size-7 items-center justify-center rounded-full bg-secondary text-xs font-semibold text-foreground">
                        {item.step}
                      </span>
                      <span className="text-base font-medium text-foreground group-hover:text-primary transition-colors">
                        {item.title}
                      </span>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="text-muted-foreground pl-10 pr-2">
                    <div className="space-y-3 pt-1 pb-2">
                      <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-primary">
                        <Badge variant="outline" className="bg-secondary/60 text-foreground border-border text-[11px] py-0 px-2">
                          Estimated Duration: {item.timing}
                        </Badge>
                      </div>
                      <p className="text-sm leading-relaxed text-foreground/80">
                        {item.description}
                      </p>
                      <div className="flex items-start gap-2 bg-secondary/50 rounded-lg p-3 text-xs text-muted-foreground border border-border/70">
                        <CheckCircle2 className="size-4 text-primary shrink-0 mt-0.5" />
                        <span><strong className="text-foreground">Homeowner Action:</strong> {item.checklistNote}</span>
                      </div>
                    </div>
                  </AccordionContent>
                </AccordionItem>)}
            </Accordion>
          </motion.div>

          {/* Proactive Craftsman Tip Callout Card (5 Columns) */}
          <motion.div className="lg:col-span-5 space-y-6" initial={shouldReduceMotion ? {
          opacity: 1
        } : {
          opacity: 0,
          y: 16
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.4,
          delay: shouldReduceMotion ? 0 : 0.1,
          ease: [0.16, 1, 0.3, 1]
        }}>
            <Card className="border border-border bg-secondary/40 backdrop-blur-md relative overflow-hidden">
              <div className="absolute top-0 left-0 right-0 h-1 bg-primary" />
              <CardHeader className="pb-4">
                <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1">
                  <Home className="size-4 text-primary" />
                  <span>Practical Craftsman Tip</span>
                </div>
                <CardTitle className="text-2xl font-serif text-foreground font-semibold">
                  Living on-site versus temporary relocation
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4 text-sm text-muted-foreground leading-relaxed">
                <p>
                  For single-bathroom remodels or whole-home structural work, moving off-site for critical 2–3 week phases significantly lowers family stress and allows our build teams to run multiple trades simultaneously.
                </p>
                <p>
                  If you choose to stay during kitchen remodels, we establish a dedicated temporary kitchenette complete with water filtration, refrigeration space, and daily dust-tight air scrubbing.
                </p>

                <div className="pt-4 border-t border-border space-y-3">
                  <div className="text-xs font-medium text-foreground flex items-center gap-2">
                    <FileText className="size-4 text-primary" />
                    <span>Comprehensive Planning Consultation</span>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Discuss your schedule, budget targets, and design ideas with our Decatur construction leads.
                  </p>

                  <div className="pt-2">
                    <Button asChild size="lg" className="w-full justify-center bg-primary text-primary-foreground hover:bg-primary/90 shadow-sm">
                      <Link to="/ContactPage" className="inline-flex items-center gap-2">
                        <span>Book a Planning Consultation</span>
                        <ArrowRight className="size-4" />
                      </Link>
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Neighborhood Assurance Strip */}
            <div className="rounded-xl border border-border bg-card p-5 flex items-center gap-4">
              <div className="flex size-11 shrink-0 items-center justify-center rounded-lg bg-secondary text-primary">
                <Compass className="size-5" />
              </div>
              <div className="space-y-1">
                <h4 className="text-sm font-semibold text-foreground font-serif">
                  Decatur Neighborhood Expertise
                </h4>
                <p className="text-xs text-muted-foreground">
                  Specialized in historic bungalows across Oakhurst, Winnona Park, and Great Lakes.
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>;
}